import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { match } from 'ts-pattern'

import { Footer } from '@frame/ui-components/footer'
import { HighlightedEmployerName } from '@frame/ui-components/highlighted-employer-name'
import { RadioGroup, RadioOption } from '@frame/ui-components/radio-group'
import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { Body } from '@frame/ui-primitives/body'
import { Button } from '@frame/ui-primitives/button'
import { H2 } from '@frame/ui-primitives/h2'
import { P } from '@frame/ui-primitives/p'

import { useDocumentsFlowContext } from '@documents/contexts/documents-flow-context'
import { DocumentTypeOption } from '@documents/types'
import { FeatureFlagList } from '@frame/lib/api/common/types'
import { useDirectTask } from '@frame/lib/hooks/useDirectTask'
import { useFeatureEnabled } from '@frame/lib/hooks/useFeatures'
import { useHeaderTitle } from '@frame/lib/hooks/useHeaderTitle'
import { useShowExitPrompt } from '@frame/lib/hooks/useShowExitPrompt'
import { useStrategies } from '@frame/lib/hooks/useStrategies'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'

export function SelectDocumentType() {
  const navigate = useNavigate()

  const isManualDocumentClassificationEnabled = useFeatureEnabled(FeatureFlagList.ManualDocumentClassification)
  const directTask = useDirectTask()
  const title = useHeaderTitle()
  const showExitPrompt = useShowExitPrompt()
  const { selectedDocumentType, updateSelectedDocumentType, clearOtherDocumentTypeSelections, hideW2s } =
    useDocumentsFlowContext()
  const { bilEnabledStrategesNoDocuments } = useStrategies()

  const employerName = directTask?.employer_name || 'your employer'
  const isContinueDisabled = !selectedDocumentType
  const showNoneOfTheAboveOption = bilEnabledStrategesNoDocuments?.length > 0
  const shouldShowOfferLetterOption = isManualDocumentClassificationEnabled && Boolean(directTask?.employer_name)

  useEffect(() => {
    clearOtherDocumentTypeSelections()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function handleChange(value: DocumentTypeOption) {
    updateSelectedDocumentType(value)
  }

  function handleClick() {
    return match(selectedDocumentType)
      .with(DocumentTypeOption.PAYSTUB_OR_W2, DocumentTypeOption.OFFER_LETTER, () =>
        navigate(`${routes.uploadDocuments}/${directTask?.id}`)
      )
      .with(DocumentTypeOption.OTHER, () => navigate(`${routes.selectOtherDocumentType}/${directTask?.id}`))
      .with(DocumentTypeOption.NONE, () => navigate(`${routes.alternativeMethods}/${directTask?.id}`))
      .with(null, () => navigate(`${routes.uploadDocuments}/${directTask?.id}`))
      .exhaustive()
  }

  return (
    <>
      <WidgetHeaderWithNavigation title={title} showExitPrompt={showExitPrompt} />{' '}
      <Body data-cy={DataAttribute.DOCUMENTS_SCREEN_SELECT_DOCUMENT_TYPE} className="p-5" paddingTopOverride={0}>
        <H2 className="mb-4 text-left text-moss-950">Which documents do you have?</H2>
        <RadioGroup<DocumentTypeOption>
          name="select-document-type"
          selected={selectedDocumentType}
          onChange={handleChange}
        >
          <RadioOption id={DocumentTypeOption.PAYSTUB_OR_W2} value={DocumentTypeOption.PAYSTUB_OR_W2}>
            <P className="font-semibold">
              {hideW2s ? 'Paystubs' : 'Paystubs or W2'} from <HighlightedEmployerName employerName={employerName} />
            </P>
          </RadioOption>
          {shouldShowOfferLetterOption && (
            <RadioOption id={DocumentTypeOption.OFFER_LETTER} value={DocumentTypeOption.OFFER_LETTER}>
              <P className="font-semibold">Offer letter</P>
            </RadioOption>
          )}
          <RadioOption id={DocumentTypeOption.OTHER} value={DocumentTypeOption.OTHER}>
            <P className="font-semibold">Other income documents</P>
          </RadioOption>
          {showNoneOfTheAboveOption ? (
            <RadioOption id={DocumentTypeOption.NONE} value={DocumentTypeOption.NONE}>
              <P className="font-semibold">None of these</P>
            </RadioOption>
          ) : null}
        </RadioGroup>
      </Body>
      <Footer
        buttons={{
          primary: (
            <Button disabled={isContinueDisabled} onClick={handleClick}>
              Continue
            </Button>
          ),
        }}
      />
    </>
  )
}
